import React, { useState, useCallback, useEffect } from "react";
import { Spinner } from "reactstrap";
import queryString from "query-string";

import CardResult from "./components/CardResult";
import apiPublic from "../../../services/apiPublic";
import { formatDate, formatDateTime } from "../../../utils/formatDate";
import { useToast } from "../../../hooks/toast";
import autopilotLogo from "../../../assets/img/autopilot-logo.png";
import "./Tracking.css";

const TRACKING_KEY_LENGTH = 44;

const normalizeParam = (value) => {
  if (Array.isArray(value)) return value[0] || "";
  return value || "";
};

const Tracking = ({ location }) => {
  const paramsParsed = queryString.parse((location && location.search) || "");
  const chaveNf = normalizeParam(paramsParsed.chaveNf).trim();

  const [dataNf, setDataNf] = useState(null);
  const [loading, setLoading] = useState(false);
  const [logoLink, setLogoLink] = useState(null);
  const [statusMessage, setStatusMessage] = useState(null);

  const { addToast } = useToast();

  const showToast = useCallback(
    (description) => {
      addToast({
        place: "tr",
        type: "danger",
        title: "Rastreamento indisponível",
        description,
        icon: "tim-icons icon-alert-circle-exc",
      });
    },
    [addToast]
  );

  const loadLogo = useCallback(async (id) => {
    if (!id) return null;

    try {
      const result = await apiPublic.get(`/integracoes/download-logo-rastreio/${id}`);
      return result.data || null;
    } catch (_) {
      return null;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function loadNotaFiscal() {
      if (!chaveNf || chaveNf.length !== TRACKING_KEY_LENGTH) {
        setDataNf(null);
        setLogoLink(null);
        setStatusMessage("Link de rastreamento inválido.");
        setLoading(false);
        return;
      }

      setLoading(true);
      setStatusMessage(null);

      try {
        const result = await apiPublic.get("/tracking", {
          params: {
            chaveNf,
          },
        });

        if (cancelled) return;

        if (result.status === 200 && result.data.length > 0) {
          const notaFiscal = result.data.map((nf) => ({
            dt_emissao: nf.data_emissao ? formatDate(nf.data_emissao) : null,
            dt_previsao_entrega: nf.previsao_entrega
              ? formatDate(nf.previsao_entrega)
              : null,
            dt_entrega: nf.entrega ? formatDate(nf.entrega) : null,
            // PO1-2818: data/hora da última ocorrência, para o bloco
            // "Última atualização" do card.
            dt_ultima_ocorrencia:
              nf.ultimaOcorrencia && nf.ultimaOcorrencia.data
                ? formatDateTime(nf.ultimaOcorrencia.data)
                : null,
            ...nf,
          }));

          const logo = await loadLogo(result.data[0].id_integracao_ecommerce);
          if (cancelled) return;

          setLogoLink(logo);
          setDataNf(notaFiscal);
          setLoading(false);
          return;
        }

        setDataNf(null);
        setLogoLink(null);
        setStatusMessage("Não encontramos uma entrega para este link.");
        setLoading(false);
      } catch (_) {
        if (!cancelled) {
          setDataNf(null);
          setLogoLink(null);
          setStatusMessage("Não conseguimos carregar sua entrega agora.");
          setLoading(false);
          showToast("Tente atualizar a página em alguns instantes.");
        }
      }
    }

    loadNotaFiscal();

    return () => {
      cancelled = true;
    };
  }, [chaveNf, loadLogo, showToast]);

  return (
    <div className="content public-tracking-content">
      <main className="public-tracking-page">
        <header className="public-tracking-hero">
          <div className="public-tracking-brand">
            {logoLink ? (
              <img src={logoLink} alt="Logo da loja" />
            ) : (
              <img
                className="public-tracking-brand__robot"
                src={autopilotLogo}
                alt=""
                aria-hidden="true"
              />
            )}
            <div>
              <span className="public-tracking-eyebrow">Rastreamento</span>
              <h1>Acompanhe sua entrega</h1>
              <p>
                Veja o status atualizado, a previsão e o histórico da entrega
                vinculada ao seu pedido.
              </p>
            </div>
          </div>
        </header>

        {loading ? (
          <section className="public-tracking-state">
            <Spinner color="info" />
            <strong>Buscando sua entrega...</strong>
            <span>Estamos consultando as informações mais recentes.</span>
          </section>
        ) : dataNf ? (
          <CardResult data={dataNf} chaveNf={chaveNf} />
        ) : (
          <section className="public-tracking-state public-tracking-state--empty">
            <i className="tim-icons icon-alert-circle-exc" />
            <strong>{statusMessage || "Entrega não encontrada."}</strong>
            <span>
              Confira se o link recebido por e-mail está completo ou tente
              novamente mais tarde.
            </span>
          </section>
        )}
      </main>
    </div>
  );
};

export default Tracking;
